import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useT } from '../i18n/LanguageContext';
import shippingDocumentService, { shippingId } from '../services/shippingDocumentService';
import useShippingRequest from '../hooks/useShippingRequest';
import { saveShippingDocument } from '../utils/shippingDocumentDownload';

export const shippingDownloadError = (error) => {
  const status = error?.response?.status;
  if (status === 404 || status === 403)
    return 'This shipping document is unavailable. Refresh and try again.';
  if (status === 413)
    return 'This shipping document is too large to download. Contact MedEffects for help.';
  if (status === 409) return 'This shipping document changed. Refresh and review it again.';
  return 'Could not download the shipping document. Please try again.';
};

export default function ShippingDocuments({ orderId, shipmentId, adminDistributorId }) {
  const t = useT();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const busyRef = useRef(false);
  const [error, setError] = useState('');
  const { begin, version } = useShippingRequest(
    `${adminDistributorId || ''}:${orderId || ''}:${shipmentId || ''}`
  );
  const load = useCallback(async () => {
    const request = begin();
    busyRef.current = false;
    setBusy('');
    setDocuments([]);
    setError('');
    setLoading(true);
    try {
      if (!!orderId === !!shipmentId || !shippingId(orderId || shipmentId)) throw new Error();
      const data = await shippingDocumentService.search(
        {
          orderId,
          shipmentId,
          ...(adminDistributorId
            ? { businessContext: 'distributor', distributorId: adminDistributorId }
            : {}),
        },
        request
      );
      if (request.isCurrent()) setDocuments(data.documents);
    } catch {
      if (request.isCurrent()) setError('Could not load shipping documents. Please try again.');
    } finally {
      if (request.isCurrent()) setLoading(false);
    }
  }, [begin, orderId, shipmentId, adminDistributorId]);
  useEffect(() => {
    load();
  }, [load, version]);
  const download = async (row) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(row._id);
    setError('');
    const request = begin();
    try {
      const response = await shippingDocumentService.download(
        {
          orderId,
          shipmentId,
          ...(adminDistributorId
            ? { businessContext: 'distributor', distributorId: adminDistributorId }
            : {}),
        },
        row._id,
        request
      );
      if (request.isCurrent())
        await saveShippingDocument(response, { isCurrent: request.isCurrent });
    } catch (failure) {
      if (request.isCurrent()) setError(shippingDownloadError(failure));
    } finally {
      if (request.isCurrent()) {
        busyRef.current = false;
        setBusy('');
      }
    }
  };
  return (
    <section
      aria-label={t('Shipping documents')}
      className="rounded-lg border border-[#e2e8f0] p-3 my-4"
    >
      <div className="flex items-center justify-between gap-3 mb-2">
        <h3 className="text-sm font-semibold">{t('Shipping documents')}</h3>
        <button
          type="button"
          disabled={loading || !!busy}
          onClick={load}
          className="text-xs text-[#0089ff] disabled:opacity-50"
        >
          {t('Refresh documents')}
        </button>
      </div>
      {error && (
        <p role="alert" className="text-sm text-red-700 mb-2">
          {t(error)}
        </p>
      )}
      {loading ? (
        <p role="status" className="text-xs text-slate-500">
          {t('Loading shipping documents...')}
        </p>
      ) : !documents.length && !error ? (
        <p className="text-xs text-slate-500">{t('No shipping documents are available yet.')}</p>
      ) : (
        <ul className="space-y-2">
          {documents.map((row, index) => (
            <li key={row._id} className="flex items-center justify-between gap-3">
              <span className="text-xs">
                {t('Shipping slip {n}', { n: index + 1 })}{' '}
                <span className="text-slate-500">({Math.ceil(row.byteLength / 1024)} KB)</span>
              </span>
              <button
                type="button"
                disabled={!!busy}
                onClick={() => download(row)}
                aria-label={t('Download shipping slip {n}', { n: index + 1 })}
                className="shrink-0 text-xs font-medium text-[#0089ff] disabled:opacity-50"
              >
                {t(busy === row._id ? 'Preparing PDF...' : 'Download PDF')}
              </button>
            </li>
          ))}
        </ul>
      )}
      {!!busy && (
        <p role="status" className="mt-2 text-xs text-slate-500">
          {t('Preparing shipping document...')}
        </p>
      )}
    </section>
  );
}
